/**
 * ScanHistorySection — shows all scans for a shop with status, progress,
 * timing, view links, and delete/retry actions.
 * Embedded in ShopDetailPage below the Sections block.
 */
import { useState } from 'react';
import { ScanRecord } from '../../stores/shopStore';
import './ScanHistorySection.css';

interface Props {
  shopId: string;
  scans: ScanRecord[];
  onRefresh: () => void;
  onDelete: (shopId: string, scanId: string) => Promise<void>;
  onRetry: (shopId: string, scanId: string) => Promise<void>;
}

export default function ScanHistorySection({ shopId, scans, onRefresh, onDelete, onRetry }: Props) {
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Sort by startedAt descending (newest first)
  const sorted = [...scans].sort((a, b) => {
    const ta = a.startedAt ? new Date(a.startedAt).getTime() : 0;
    const tb = b.startedAt ? new Date(b.startedAt).getTime() : 0;
    return tb - ta;
  });

  async function handleRefresh() {
    setRefreshing(true);
    await onRefresh();
    setRefreshing(false);
  }

  async function handleDelete(scanId: string) {
    await onDelete(shopId, scanId);
    setConfirmDeleteId(null);
  }

  async function handleRetry(scanId: string) {
    setRetrying(scanId);
    await onRetry(shopId, scanId);
    setRetrying(null);
  }

  function formatDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  }

  function formatProcessingTime(pt: string | null | undefined): string {
    if (!pt) return '—';
    // Backend sends e.g. "0:05:23.456789" (Python timedelta str)
    const match = pt.match(/^(\d+):(\d{2}):(\d{2})/);
    if (match) {
      const h = parseInt(match[1], 10);
      const m = parseInt(match[2], 10);
      const s = parseInt(match[3], 10);
      if (h > 0) return `${h}h ${m}m ${s}s`;
      if (m > 0) return `${m}m ${s}s`;
      return `${s}s`;
    }
    return pt;
  }

  function statusIcon(status: string): string {
    switch (status) {
      case 'uploading': return '⬆';
      case 'queued': return '⏳';
      case 'processing': return '⚙';
      case 'ready': return '✓';
      case 'failed': return '✗';
      default: return '?';
    }
  }

  function statusText(status: string): string {
    switch (status) {
      case 'uploading': return 'Uploading';
      case 'queued': return 'Queued';
      case 'processing': return 'Processing';
      case 'ready': return 'Ready';
      case 'failed': return 'Failed';
      default: return status;
    }
  }

  return (
    <section className="shop-detail-section">
      <h2 className="shop-detail-section-title">
        Scan History
        <span className="shop-detail-section-count">{scans.length}</span>
        <button
          className="sh-refresh-btn"
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh scan list"
        >
          {refreshing ? '⟳' : '↻'}
        </button>
      </h2>

      {sorted.length === 0 ? (
        <p className="sh-empty">No scans yet. Use the 📷 Scan button on a section above to start.</p>
      ) : (
        <div className="sh-list">
          {sorted.map((scan) => {
            const uploadPct = scan.frameCount > 0
              ? Math.round((scan.framesUploaded / scan.frameCount) * 100)
              : 0;

            return (
              <div key={scan.id} className={`sh-card sh-card-${scan.status}`}>
                {/* Status badge */}
                <div className={`sh-status-col sh-status-${scan.status}`}>
                  <span className="sh-status-icon">{statusIcon(scan.status)}</span>
                  <span className="sh-status-text">{statusText(scan.status)}</span>
                </div>

                {/* Info column */}
                <div className="sh-info-col">
                  <div className="sh-section-name">{scan.sectionName || scan.sectionId}</div>
                  <div className="sh-date">{formatDate(scan.startedAt)}</div>

                  {/* Upload progress bar */}
                  {scan.status === 'uploading' && (
                    <div className="sh-progress">
                      <div className="sh-progress-bar">
                        <div className="sh-progress-fill" style={{ width: `${uploadPct}%` }} />
                      </div>
                      <span className="sh-progress-text">
                        {scan.framesUploaded}/{scan.frameCount} frames ({uploadPct}%)
                      </span>
                    </div>
                  )}

                  {/* Processing time */}
                  {scan.status === 'ready' && scan.processingTime && (
                    <div className="sh-processing-time">
                      Processed in {formatProcessingTime(scan.processingTime)}
                    </div>
                  )}

                  {/* Error message */}
                  {scan.status === 'failed' && scan.error && (
                    <div className="sh-error-msg" title={scan.error}>
                      {scan.error.length > 80 ? scan.error.substring(0, 80) + '…' : scan.error}
                    </div>
                  )}

                  {/* Frame count for non-uploading */}
                  {scan.status !== 'uploading' && (
                    <div className="sh-frame-count">{scan.framesUploaded || scan.frameCount} frames</div>
                  )}
                </div>

                {/* Actions column */}
                <div className="sh-actions-col">
                  {/* View link — only when ready */}
                  {scan.status === 'ready' && (
                    <a
                      className="sh-btn sh-btn-view"
                      href={`/?shopid=${scan.datasetId || scan.id}`}
                      target="_blank"
                      rel="noopener"
                      title="View in 3D"
                    >
                      👁
                    </a>
                  )}

                  {/* Retry — only when failed */}
                  {scan.status === 'failed' && (
                    <button
                      className="sh-btn sh-btn-retry"
                      onClick={() => handleRetry(scan.id)}
                      disabled={retrying === scan.id}
                      title="Retry processing"
                    >
                      {retrying === scan.id ? '⟳' : '🔁'}
                    </button>
                  )}

                  {/* Delete */}
                  {confirmDeleteId === scan.id ? (
                    <div className="sh-confirm-delete">
                      <button className="sh-btn sh-btn-confirm-yes" onClick={() => handleDelete(scan.id)}>✓</button>
                      <button className="sh-btn sh-btn-confirm-no" onClick={() => setConfirmDeleteId(null)}>✕</button>
                    </div>
                  ) : (
                    <button
                      className="sh-btn sh-btn-delete"
                      onClick={() => setConfirmDeleteId(scan.id)}
                      title="Delete scan"
                    >
                      🗑
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
